import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface ReadingProgressBarProps {
  previewMode?: boolean;
  activePath?: string;
}

export const ReadingProgressBar: React.FC<ReadingProgressBarProps> = ({
  previewMode = false,
  activePath
}) => {
  const [progress, setProgress] = useState<number>(0);
  const location = useLocation();

  const currentPath = previewMode ? (activePath || '') : location.pathname;

  // Verifica se o usuário está visualizando a leitura de um artigo específico
  const isArticlePage = Boolean(
    currentPath &&
    (currentPath.startsWith('/artigo/') || currentPath.startsWith('/artigos/')) &&
    currentPath !== '/artigos' &&
    currentPath !== '/artigos/'
  );

  const calculateProgress = useCallback(() => {
    // Alvo prioritário: o container com o corpo e conteúdo do artigo
    const targetElement = document.getElementById('article-main-content') || document.querySelector('.prose');
    const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    const windowHeight = window.innerHeight || document.documentElement.clientHeight || 800;

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const elementTop = rect.top + scrollTop;
      const elementBottom = elementTop + (targetElement as HTMLElement).offsetHeight;

      // Distância que o scroll precisa percorrer para que o final do artigo chegue à tela
      const targetScrollDistance = elementBottom - windowHeight;

      if (targetScrollDistance > 0) {
        const current = (scrollTop / targetScrollDistance) * 100;
        return Math.min(100, Math.max(0, current));
      } else {
        // Artigo cabe inteiramente na tela inicial
        return 100;
      }
    }

    // Fallback: cálculo baseado no scroll total do documento se o elemento ainda estiver carregando
    const totalScroll = document.documentElement.scrollHeight - windowHeight;
    if (totalScroll > 0) {
      return Math.min(100, Math.max(0, (scrollTop / totalScroll) * 100));
    }

    return 0;
  }, []);

  useEffect(() => {
    if (!isArticlePage) {
      setProgress(0);
      return;
    }

    let ticking = false;

    const handleScrollOrResize = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setProgress(calculateProgress());
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize, { passive: true });

    // Atualização imediata
    handleScrollOrResize();

    // Atualização após renderização inicial e carregamento de imagens/fontes
    const timer1 = setTimeout(handleScrollOrResize, 150);
    const timer2 = setTimeout(handleScrollOrResize, 600);

    // ResizeObserver no elemento do artigo para reagir a carregamento assíncrono de imagens
    let resizeObserver: ResizeObserver | null = null;
    const targetElement = document.getElementById('article-main-content') || document.querySelector('.prose');
    if (targetElement && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        handleScrollOrResize();
      });
      resizeObserver.observe(targetElement);
    }

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
      clearTimeout(timer1);
      clearTimeout(timer2);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isArticlePage, currentPath, calculateProgress]);

  if (!isArticlePage) {
    return null;
  }

  return (
    <div
      className="absolute top-full left-0 right-0 h-[3px] bg-stone-950/60 backdrop-blur-xs overflow-hidden pointer-events-none z-50 shadow-[0_1px_4px_rgba(0,0,0,0.5)]"
      role="progressbar"
      aria-label="Progresso de leitura do artigo"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {/* Barra de progresso com gradiente e transição suave */}
      <div
        className="h-full bg-gradient-to-r from-primary via-accent to-red-500 transition-[width] duration-150 ease-out relative shadow-[0_0_12px_rgba(193,18,31,0.85)]"
        style={{ width: `${progress}%` }}
      >
        {/* Ponto luminoso sutil na ponta da barra indicando o avanço */}
        {progress > 0 && progress < 100 && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.95),0_0_14px_rgba(230,57,70,1)]" />
        )}
      </div>
    </div>
  );
};

export default ReadingProgressBar;
